import { useCallback, useMemo } from "react";

import {
    TokenPositionExpanded,
    OracleQuote,
    RaydiumPositionExpanded,
    TokenListMetadata,
    ParsedRaydiumPool,
    ParsedRaydiumPosition
} from "@bridgesplit/abf-sdk";
import { removeDuplicates, lamportsToUiAmount, filterTruthy, formatAddress, Result } from "@bridgesplit/utils";
import { skipToken } from "@reduxjs/toolkit/query";
import {
    getMultipleAccountsInfo,
    LiquidityMath,
    PositionUtils,
    SqrtPriceMath,
    Tick,
    TickArrayLayout,
    TickUtils
} from "@raydium-io/raydium-sdk-v2";
import BN from "bn.js";
import { Connection, PublicKey } from "@solana/web3.js";

import { useClmmPositionsQuery, useClaimableFeeQuery } from "../reducers";
import { useOraclePrices, getUsdAmountFromUsdPrice } from "./pricing";
import { AssetTypeIdentifier, RaydiumPoolData, RaydiumPositionData, TokenBalanceExpanded } from "../types";
import { useUserUnrecognizedAssets } from "./assets";
import { useTokenListContext } from "./token-list";

export const RAYDIUM_MAX_TICK_INDEX = 887272;
export const RAYDIUM_MIN_TICK_INDEX = -887272;

export function useUserRaydiumPositions(options?: { skip?: boolean }) {
    const { data: walletAssets } = useUserUnrecognizedAssets(!!options?.skip);

    const nftMints = useMemo(() => {
        return walletAssets?.filter((d) => d.tokenAmount.decimals === 0).map((d) => d.mint);
    }, [walletAssets]);
    return useRaydiumPositions(nftMints, options);
}

export function useRaydiumPositions(nftMints: string[] | undefined, options?: { skip?: boolean }) {
    const {
        data: rawData,
        isLoading: clmmPositionsLoading,
        isFetching
    } = useClmmPositionsQuery(nftMints ? removeDuplicates(nftMints) : skipToken, {
        skip: !nftMints?.length || options?.skip
    });

    const mints = Object.values(rawData?.raydiumPools ?? {})
        .map((w) => [w.tokenMint0, w.tokenMint1])
        .flat();

    const { getOracle, isLoading: pricesLoading } = useOraclePrices(mints);
    const { getMetadata, isLoading: metadataLoading } = useTokenListContext();

    const isLoading = clmmPositionsLoading || pricesLoading || metadataLoading;

    const getTokenDetail = useCallback(
        (mint: string, lamportAmount: number): (TokenPositionExpanded & { oracle: OracleQuote }) | undefined => {
            const metadata = getMetadata(mint);
            const oracle = getOracle(mint);
            if (!metadata || !oracle) return undefined;
            const amount = lamportsToUiAmount(lamportAmount, metadata.decimals);
            return {
                mint,
                decimals: metadata.decimals,
                metadata,
                usdPrice: oracle.usdPrice,
                oracle,
                usdAmount: oracle.getUsdAmount(amount),
                amount
            };
        },
        [getMetadata, getOracle]
    );

    //Calculated position amounts based on totalLiquidity

    const data = useMemo(() => {
        if (nftMints?.length === 0) return [];
        if (!rawData || isLoading) return undefined;
        const allPositions = Object.values(rawData.raydiumPoolsToPositions).flat();

        return allPositions
            .map((position): RaydiumPositionExpanded | null => {
                const pool = rawData.raydiumPools[position.poolId];
                if (!pool) return null;
                const tokenAmounts = getTokenAmountsFromLiquidity(position, pool);
                const tokenA = getTokenDetail(pool.tokenMint0, tokenAmounts[pool.tokenMint0]);
                const tokenB = getTokenDetail(pool.tokenMint1, tokenAmounts[pool.tokenMint1]);
                const poolMetadata = getMetadata(position.poolId);

                if (!tokenA || !tokenB || !poolMetadata) return null;
                const totalPrice = tokenA.usdAmount + tokenB.usdAmount;

                const oracle: OracleQuote = {
                    usdPrice: totalPrice,
                    marketPrice: totalPrice,
                    getUsdAmount: (...params) =>
                        getUsdAmountFromUsdPrice(
                            totalPrice,
                            tokenA.oracle.uncertainty + tokenB.oracle.uncertainty,
                            ...params
                        ),
                    oracleAccount: position.poolId,
                    baseQuotes: [...tokenA.oracle.baseQuotes, ...tokenB.oracle.baseQuotes],
                    uncertainty: tokenA.oracle.uncertainty + tokenB.oracle.uncertainty
                };

                return {
                    totalPrice,
                    poolAddress: position.poolId,
                    poolMetadata,
                    tokenA,
                    tokenB,
                    pool,
                    position,
                    oracle
                };
            })
            .filter(filterTruthy);
    }, [getMetadata, getTokenDetail, isLoading, nftMints?.length, rawData]);

    return { data, isLoading, isFetching };
}

export const raydiumPositionToToken = (raydiumPosition: RaydiumPositionExpanded): TokenBalanceExpanded => {
    const {
        poolMetadata,
        position: { nftMint: mint }
    } = raydiumPosition;
    return {
        amount: 1,
        metadata: {
            ...poolMetadata,
            mint,
            name: `Raydium LP (${formatAddress(mint)})`
        },
        key: mint,
        clmmPosition: raydiumPosition
    };
};

export function isRaydiumMetadata(metadata: TokenListMetadata | undefined) {
    if (!metadata) return false;
    return metadata.assetType === AssetTypeIdentifier.RaydiumPosition;
}

export function getRaydiumTokenAmountsFromPoolData(positionExpanded: RaydiumPositionExpanded, sqrtPriceX64: BN) {
    const { liquidity, tickUpperIndex, tickLowerIndex } = positionExpanded.position;

    const lowerSqrtPriceX64 = SqrtPriceMath.getSqrtPriceX64FromTick(tickLowerIndex);
    const upperSqrtPriceX64 = SqrtPriceMath.getSqrtPriceX64FromTick(tickUpperIndex);

    const amounts = LiquidityMath.getAmountsFromLiquidity(
        sqrtPriceX64,
        lowerSqrtPriceX64,
        upperSqrtPriceX64,
        liquidity,
        true //Round up
    );
    return {
        [positionExpanded.tokenA.mint]: amounts.amountA.toNumber(),
        [positionExpanded.tokenB.mint]: amounts.amountB.toNumber()
    };
}
function getTokenAmountsFromLiquidity(
    position: ParsedRaydiumPosition,
    pool: ParsedRaydiumPool
): Record<string, number> {
    const { liquidity, tickUpperIndex, tickLowerIndex } = position;
    const { sqrtPriceX64 } = pool;

    const lowerSqrtPriceX64 = SqrtPriceMath.getSqrtPriceX64FromTick(tickLowerIndex);
    const upperSqrtPriceX64 = SqrtPriceMath.getSqrtPriceX64FromTick(tickUpperIndex);

    const amounts = LiquidityMath.getAmountsFromLiquidity(
        sqrtPriceX64,
        lowerSqrtPriceX64,
        upperSqrtPriceX64,
        liquidity,
        true //Round up
    );

    return {
        [pool.tokenMint0]: amounts.amountA.toNumber(),
        [pool.tokenMint1]: amounts.amountB.toNumber()
    };
}

export function parseRaydiumAmounts(raydiumPosition: RaydiumPositionExpanded) {
    const { position, tokenA, tokenB } = raydiumPosition;

    // Raydium fee amounts are stored directly in position
    const feeAmounts = {
        [tokenA.mint]: position.tokenFeesOwed0.toNumber(),
        [tokenB.mint]: position.tokenFeesOwed1.toNumber()
    };

    return Object.entries(feeAmounts).map(([mint, rawAmount]) => {
        const token = mint === tokenA.mint ? tokenA : tokenB;
        const uiAmount = lamportsToUiAmount(rawAmount, token.metadata.decimals);
        const usdAmount = uiAmount * token.usdPrice;

        return { mint, uiAmount, usdAmount };
    });
}

export function useClaimableRaydiumFee(
    positionExpanded?: RaydiumPositionExpanded,
    poolData?: RaydiumPoolData | null,
    positionData?: RaydiumPositionData | null
) {
    const skip = !positionExpanded || !poolData || !positionData;

    const { data, isLoading } = useClaimableFeeQuery(
        skip
            ? skipToken
            : {
                  positionExpanded,
                  poolData,
                  positionData
              },
        { skip }
    );

    return { data: data ? data : undefined, isLoading };
}

//Used in raydium external api

export type ClaimableRaydiumFeeResult = {
    feesUsd: number;
    liquidity: BN;
};
export async function getClaimableRaydiumFee(
    connection: Connection,
    positionExpanded: RaydiumPositionExpanded,
    poolData: RaydiumPoolData,
    positionData: RaydiumPositionData
): Promise<Result<ClaimableRaydiumFeeResult>> {
    try {
        if (!poolData || !positionData) {
            return Result.ok({ liquidity: new BN(0), feesUsd: 0 });
        }

        const ticksResult = await getTicksFromPosition(connection, poolData, positionData.poolId, positionData);

        if (ticksResult.isErr()) {
            return Result.err(ticksResult.unwrapErrMessage());
        }

        const { lowerTick, upperTick } = ticksResult.unwrap();

        const fees = PositionUtils.GetPositionFeesV2(poolData, positionData, lowerTick, upperTick);

        const tokenA = positionExpanded.tokenA;
        const tokenB = positionExpanded.tokenB;

        const feeAUi = lamportsToUiAmount(fees.tokenFeeAmountA.toNumber(), tokenA.decimals);
        const feeBUi = lamportsToUiAmount(fees.tokenFeeAmountB.toNumber(), tokenB.decimals);

        const feeAUsd = feeAUi * (tokenA.usdPrice ?? 0);
        const feeBUsd = feeBUi * (tokenB.usdPrice ?? 0);

        const lowerSqrtPriceX64 = SqrtPriceMath.getSqrtPriceX64FromTick(positionData.tickLower);
        const upperSqrtPriceX64 = SqrtPriceMath.getSqrtPriceX64FromTick(positionData.tickUpper);

        const liquidity = LiquidityMath.getLiquidityFromTokenAmounts(
            poolData.sqrtPriceX64,
            lowerSqrtPriceX64,
            upperSqrtPriceX64,
            fees.tokenFeeAmountA,
            fees.tokenFeeAmountB
        );

        return Result.ok({ liquidity, feesUsd: feeAUsd + feeBUsd });
    } catch (error) {
        return Result.err(error);
    }
}

async function getTicksFromPosition(
    connection: Connection,
    poolData: RaydiumPoolData,
    poolId: PublicKey,
    positionData: RaydiumPositionData
): Promise<Result<{ lowerTick: Tick; upperTick: Tick }>> {
    try {
        const tickArrayAddressLower = TickUtils.getTickArrayAddressByTick(
            poolData.programId,
            poolId,
            positionData.tickLower,
            poolData.tickSpacing
        );

        const tickArrayAddressUpper = TickUtils.getTickArrayAddressByTick(
            poolData.programId,
            poolId,
            positionData.tickUpper,
            poolData.tickSpacing
        );

        const accounts = await getMultipleAccountsInfo(connection, [tickArrayAddressLower, tickArrayAddressUpper]);

        if (!accounts[0] || !accounts[1]) {
            return Result.err("Failed to fetch tick array accounts");
        }

        const lowerArray = TickArrayLayout.decode(accounts[0].data);
        const upperArray = TickArrayLayout.decode(accounts[1].data);

        const lowerOffset = TickUtils.getTickOffsetInArray(positionData.tickLower, poolData.tickSpacing);
        const upperOffset = TickUtils.getTickOffsetInArray(positionData.tickUpper, poolData.tickSpacing);

        const lowerTick = lowerArray.ticks[lowerOffset];
        const upperTick = upperArray.ticks[upperOffset];

        if (!lowerTick || !upperTick) {
            return Result.err("Tick not found in decoded tick array");
        }

        return Result.ok({ lowerTick, upperTick });
    } catch (error) {
        return Result.err(error);
    }
}
